<template>
	<div v-if="displayDataEdit">
		<b-container>
			<!-- Development Estimates -->
			<b-row> 
				<b-col cols="8">
					<h3> Development Estimates </h3>
				</b-col>
				<b-col v-if="!editMode" cols="2" />
				<b-col
					v-if="!editMode"
					cols="1"
					class="date-icon"
				>
					<b-icon 
						v-b-tooltip.hover.top="formatDate(displayDataEdit.developmentEstimatesAsOfDate)"
						icon="clock"
					/>
				</b-col>
				<b-col
					v-if="editMode"
					cols="3"
					class="date-picker"
				>
					<b-form-datepicker
						id="datepicker-development"
						v-model="displayDataEdit.developmentEstimatesAsOfDate"
						size="sm" 
						locale="en" 
						placeholder="Choose a date"
						:date-format-options="{ year: 'numeric', month: 'numeric', day: 'numeric' }"
					/>
				</b-col>
				<b-col
					cols="1"
					class="edit-button"
				>
					<b-button variant="link" @click="edit()">
						Edit
					</b-button>
				</b-col>
			</b-row>

			<b-row>
				<b-col>
					<field-input
						v-model="displayDataEdit.developmentCosts"
						:mode="editMode"
						:step="0.01"
						label="Development Costs"
						unit="$"
					/>
				</b-col>
			</b-row>

			<b-row> <div style="height: 12px;" /> </b-row>

			<b-row>
				<b-col>
					<field-input
						v-model="displayDataEdit.hardCosts"
						:mode="editMode"
						:step="0.01"
						label="Hard Costs"
						unit="$"
					/>
				</b-col>
				<b-col>
					<field-input
						v-model="displayDataEdit.softCosts"
						:mode="editMode"
						:step="0.01"
						label="Soft Costs"
						unit="$"
					/>
				</b-col>
			</b-row>

			<b-row> <div style="height: 12px;" /> </b-row>

			<b-row>
				<b-col>
					<field-input
						v-model="displayDataEdit.landCosts"
						:mode="editMode"
						:step="0.01"
						label="Land Costs"
						unit="$"
					/>
				</b-col>
				<b-col>
					<field-input
						v-model="displayDataEdit.otherCosts"
						:mode="editMode"
						:step="0.01"
						label="Other Costs"
						unit="$"
					/>
				</b-col>
			</b-row>

			<!-- Steady State Projections -->
			<b-row> 
				<b-col cols="9">
					<h3> Steady State Projections </h3>
				</b-col>
				<b-col v-if="!editMode" cols="2" />
				<b-col
					v-if="!editMode"
					cols="1"
					class="date-icon"
				>
					<b-icon 
						v-b-tooltip.hover.top="formatDate(displayDataEdit.steadyStateProjectionsAsOfDate)"
						icon="clock"
					/>
				</b-col>
				<b-col
					v-if="editMode"
					cols="3"
					style="padding: 0px;"
				>
					<b-form-datepicker
						id="datepicker-steady"
						v-model="displayDataEdit.steadyStateProjectionsAsOfDate"
						size="sm" 
						locale="en" 
						placeholder="Choose a date"
						:date-format-options="{ year: 'numeric', month: 'numeric', day: 'numeric' }"
					/>
				</b-col>
			</b-row>

			<b-row>
				<b-col>
					<field-input
						v-model="displayDataEdit.annualRecurringRevenue"
						:mode="editMode"
						:step="0.01"
						label="Annual Recurring Revenue"
						unit="$"
					/>
				</b-col>
			</b-row>

			<b-row> <div style="height: 12px;" /> </b-row>

			<b-row>
				<b-col>
					<field-input
						v-model="displayDataEdit.annualRecurringExpenses"
						:mode="editMode"
						:step="0.01"
						label="Annual Recurring Expenses"
						unit="$"
					/>
				</b-col>
			</b-row>

			<b-row> <div style="height: 12px;" /> </b-row>

			<b-row>
				<b-col>
					<field-input
						v-model="displayDataEdit.annualNetOperatingIncome"
						:mode="editMode"
						:step="0.01"
						label="Annual Net Operating Income"
						unit="$"
					/>
				</b-col>
			</b-row>

			<b-row> <div style="height: 12px;" /> </b-row>

			<!-- Development Actuals -->
			<b-row> 
				<b-col cols="9">
					<h3> Development Actuals </h3>
				</b-col>

				<b-col v-if="!editMode" cols="2" />
				<b-col
					v-if="!editMode"
					cols="1"
					class="date-icon"
				>
					<b-icon 
						v-b-tooltip.hover.top="formatDate(displayDataEdit.developmentActualsAsOfDate)"
						icon="clock"
					/>
				</b-col>

				<b-col
					v-if="editMode"
					cols="3"
					style="padding: 0px;"
				>
					<b-form-datepicker
						id="datepicker-actuals"
						v-model="displayDataEdit.developmentActualsAsOfDate"
						size="sm" 
						locale="en" 
						placeholder="Choose a date"
						:date-format-options="{ year: 'numeric', month: 'numeric', day: 'numeric' }"
					/>
				</b-col>
			</b-row>

			<b-row> <div style="height: 12px;" /> </b-row>
			<!-- Overall -->
			<b-row>
				<b-col> <b>Overall</b> </b-col>
			</b-row>
			<b-row>
				<b-col>
					<field-input
						v-model="displayDataEdit.overallBudget"
						:mode="editMode"
						:step="0.01"
						label="Budget"
						unit="$"
					/>
				</b-col>
			</b-row>

			<b-row> <div style="height: 12px;" /> </b-row>

			<b-row>
				<b-col>
					<field-input
						v-model="displayDataEdit.overallContingencyQuantity"
						:mode="editMode"
						:step="0.01"
						label="Contingency Quantity"
						unit="$"
					/>
				</b-col>
				<b-col>
					<field-input
						v-model="displayDataEdit.overallContingencyPct"
						:mode="editMode"
						:step="0.01"
						label="Contingency %"
						unit="%"
					/>
				</b-col>
			</b-row>

			<b-row> <div style="height: 12px;" /> </b-row>

			<b-row>
				<b-col>
					<field-input
						v-model="displayDataEdit.overallSpend"
						:mode="editMode"
						:step="0.01"
						label="Spend"
						unit="$"
					/>
				</b-col>
			</b-row>

			<b-row> <div style="height: 12px;" /> </b-row>
			<!-- Land Cost -->
			<b-row>
				<b-col> <b>Land Cost</b> </b-col>
			</b-row>
			<b-row>
				<b-col>
					<field-input
						v-model="displayDataEdit.landCostBudget"
						:mode="editMode"
						:step="0.01"
						label="Budget"
						unit="$"
					/>
				</b-col>
			</b-row>

			<b-row> <div style="height: 12px;" /> </b-row>

			<b-row>
				<b-col>
					<field-input
						v-model="displayDataEdit.landCostContingencyQuantity"
						:mode="editMode"
						:step="0.01"
						label="Contingency Quantity"
						unit="$"
					/>
				</b-col>
				<b-col>
					<field-input
						v-model="displayDataEdit.landCostContingencyPct"
						:mode="editMode"
						:step="0.01"
						label="Contingency %"
						unit="%"
					/>
				</b-col>
			</b-row>

			<b-row> <div style="height: 12px;" /> </b-row>

			<b-row>
				<b-col>
					<field-input
						v-model="displayDataEdit.landCostSpend"
						:mode="editMode"
						:step="0.01"
						label="Spend"
						unit="$"
					/>
				</b-col>
			</b-row>

			<b-row> <div style="height: 12px;" /> </b-row>
			<!-- Hard Cost -->
			<b-row>
				<b-col> <b>Hard Cost</b> </b-col>
			</b-row>
			<b-row>
				<b-col>
					<field-input
						v-model="displayDataEdit.hardCostBudget"
						:mode="editMode"
						:step="0.01"
						label="Budget"
						unit="$"
					/>
				</b-col>
			</b-row>

			<b-row> <div style="height: 12px;" /> </b-row>

			<b-row>
				<b-col>
					<field-input
						v-model="displayDataEdit.hardCostContingencyQuantity"
						:mode="editMode"
						:step="0.01"
						label="Contingency Quantity"
						unit="$"
					/>
				</b-col>
				<b-col>
					<field-input
						v-model="displayDataEdit.hardCostContingencyPct"
						:mode="editMode"
						:step="0.01"
						label="Contingency %"
						unit="%"
					/>
				</b-col>
			</b-row>

			<b-row> <div style="height: 12px;" /> </b-row>

			<b-row>
				<b-col>
					<field-input
						v-model="displayDataEdit.hardCostSpend"
						:mode="editMode"
						:step="0.01"
						label="Spend"
						unit="$"
					/>
				</b-col>
			</b-row>

			<b-row> <div style="height: 12px;" /> </b-row>
			<!-- Soft Cost -->
			<b-row>
				<b-col> <b>Soft Cost</b> </b-col>
			</b-row>
			<b-row>
				<b-col>
					<field-input
						v-model="displayDataEdit.softCostBudget"
						:mode="editMode"
						:step="0.01"
						label="Budget"
						unit="$"
					/>
				</b-col>
			</b-row>

			<b-row> <div style="height: 12px;" /> </b-row>

			<b-row>
				<b-col>
					<field-input
						v-model="displayDataEdit.softCostContingencyQuantity"
						:mode="editMode"
						:step="0.01"
						label="Contingency Quantity"
						unit="$"
					/>
				</b-col>
				<b-col>
					<field-input
						v-model="displayDataEdit.softCostContingencyPct"
						:mode="editMode"
						:step="0.01"
						label="Contingency %"
						unit="%"
					/>
				</b-col>
			</b-row>

			<b-row> <div style="height: 12px;" /> </b-row>

			<b-row>
				<b-col>
					<field-input
						v-model="displayDataEdit.softCostSpend"
						:mode="editMode"
						:step="0.01"
						label="Spend"
						unit="$"
					/>
				</b-col>
			</b-row>

			<b-row> <div style="height: 12px;" /> </b-row>
			<!-- Other Cost -->
			<b-row>
				<b-col> <b>Other Cost</b> </b-col>
			</b-row>
			<b-row>
				<b-col>
					<field-input
						v-model="displayDataEdit.otherCostBudget"
						:mode="editMode"
						:step="0.01"
						label="Budget"
						unit="$"
					/>
				</b-col>
			</b-row>

			<b-row> <div style="height: 12px;" /> </b-row>

			<b-row>
				<b-col>
					<field-input
						v-model="displayDataEdit.otherCostContingencyQuantity"
						:mode="editMode"
						:step="0.01"
						label="Contingency Quantity"
						unit="$"
					/>
				</b-col>
				<b-col>
					<field-input
						v-model="displayDataEdit.otherCostContingencyPct"
						:mode="editMode"
						:step="0.01"
						label="Contingency %"
						unit="%"
					/>
				</b-col>
			</b-row>

			<b-row> <div style="height: 12px;" /> </b-row>

			<b-row>
				<b-col>
					<field-input
						v-model="displayDataEdit.otherCostSpend"
						:mode="editMode"
						:step="0.01"
						label="Spend"
						unit="$"
					/>
				</b-col>
			</b-row>
		</b-container>
	</div>
</template>

<script>
import { mapState } from 'vuex';
import FieldInput from './FieldInput.vue';

export default {
	name: 'DevelopmentFinancial',
	components: {FieldInput},
	filters: {},
	props: {

	},
	data() {
		return {
			editMode: false,
			displayDataEdit: {}
		};
	},
	computed: {
		...mapState(['elementEditorEntityDataId']),
		formatDate() {
			return date => (!date || date === '') ? `Not updated`: new Intl.DateTimeFormat('en-US', { dateStyle: 'long' }).format(new Date(date.replace(/-/g, '/').replace(/T.+/, ''))) 
		}
	},
	watch: {
		elementEditorEntityDataId: {
			immediate: true,
			deep: true,
			handler() {
				this.getData();
			},
		},
		displayDataEdit: {
			deep: true,
			handler() {
				this.updateData();
			},
		},
		data: {
			immediate: true,
			handler(newVal) {
				this.displayDataEdit = newVal;
			},
		}
	},
	created() {
		
	},
	mounted() {
	},
	methods: {
		edit() {
			this.editMode = !this.editMode;
		},
		async getData() {
			const developmentFinancialData = await this.axios.get(
				`/entities/${this.elementEditorEntityDataId}/developmentfinancials`
			);
			this.displayDataEdit = developmentFinancialData.data;
		},
		async updateData() {
			if (this.displayDataEdit && this.displayDataEdit.id) {
				const postDevelopmentFinancialModelData = await this.axios.patch(
					`/developmentfinancials/${this.displayDataEdit.id}`,
					this.displayDataEdit
				);
			}
		}
	},
};
</script>

<style scoped>
.group-title {
	text-align: center;
}
.date-icon {
	margin-top: auto;
	margin-bottom: auto;
	padding: 0px;
    text-align: center;
}
.date-picker {
	margin-top: auto;
	margin-bottom: auto;
	padding: 0px;
}
.edit-button {
	margin-top: auto;
	margin-bottom: auto;
	padding: 0px 0px 0px 3px;
}
</style>
